import type { Ref } from 'vue'
import { ref, watch } from 'vue'

/**
 * Where a row of links' underlines grow from and shrink to when the current item changes, so the
 * change reads as one line travelling to the new item: the header nav's sweep (`navLinkBase` and
 * `trackUnderlineOrigin` in Header.vue), driven by the change of item instead of the pointer. The
 * old underline shrinks toward the new item, and the new one grows from the side facing the old.
 *
 * `vertical` is for a column of links marked by a bar down their side (the account sidebar): the
 * same travel, with "forward" meaning further down the list and the bar growing from its top or
 * bottom edge.
 *
 * `activeIndex` is -1 when no item is current. The previous index is taken synchronously, so the
 * origins are already in place in the render that swaps the underlines.
 */
export function useUnderlineSweep(activeIndex: Readonly<Ref<number>>, options: { vertical?: boolean } = {}) {
  const [start, end] = options.vertical ? ['top', 'bottom'] as const : ['left', 'right'] as const

  const previousIndex = ref(activeIndex.value)
  watch(activeIndex, (_, previous) => {
    previousIndex.value = previous
  }, { flush: 'sync' })

  function underlineOrigin(index: number): typeof start | typeof end {
    const from = previousIndex.value
    const to = activeIndex.value
    if (from < 0 || to < 0 || from === to)
      return start
    const forward = to > from
    if (index === to)
      return forward ? start : end
    return forward ? end : start
  }

  return { underlineOrigin }
}
